/**
 * Figure Draw — Documents → Order of Events & Programme panel.
 *
 * Tile heading "Figure Draw". Tagline format:
 *   "<note> <created-on> by <user>. - Type of Draw: <figure_draw_type>"
 *
 * For "Standard Draw issued as part of event notice" the system-
 * generated draw is treated as authoritative — we prepend a small
 * note explaining it goes out with the Event Notice. For the plain
 * "Standard Draw" type the user fills in the table manually.
 *
 * Hidden entirely when the comp's ``figure_draw_type`` is
 * "Figure Draw Not Required" — there's nothing useful to show.
 *
 * (Draw Group / 2-figures variants are planned but not built in this
 * pass — they show a placeholder for now.)
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Download, Shuffle, Save, RotateCcw, AlertCircle, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';


const STANDARD_TYPES = new Set([
  'Standard Draw',
  'Standard Draw issued as part of event notice',
]);

const GROUP_DRAW_TYPES = new Set([
  'Draw Group, 2 figures',
  'Draw Group, 2 Figures',
  'Draw Group, 2 figures excluding loops',
  'Draw Group, 2 Figures, no Loops',
]);

const PLACEHOLDER_TYPES = new Set([
  // Reserved for future non-implemented draw variants. Both
  // Draw-Group-2-figures variants are now surfaced by GROUP_DRAW_TYPES
  // below (Feb 2026 build).
]);


function _formatDrawWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return '';
  }
}


export default function FigureDrawPanel({ competitionId = '' }) {
  const comp = competitionId;
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
  // Immutability rule — Feb 2026
  const [lockInfo, setLockInfo] = useState(null);
  const [canUnlock, setCanUnlock] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  // Auto-collapse once the draw is fully populated (no "Not yet drawn"
  // and every foot-required row has a Left/Right pick). User can
  // manually expand to review/edit even when it's complete.
  const [manuallyExpanded, setManuallyExpanded] = useState(false);

  const fetchLocks = useCallback(async () => {
    if (!comp) { setLockInfo(null); setCanUnlock(false); return; }
    try {
      const res = await axios.get(
        `${API}/competitions/${comp}/locks`,
        { withCredentials: true }
      );
      setLockInfo(res.data.locks?.figure_draw || null);
      setCanUnlock(!!res.data.can_unlock);
    } catch {
      setLockInfo(null);
      setCanUnlock(false);
    }
  }, [comp]);

  const fetchData = useCallback(async () => {
    if (!comp) { setData(null); setRows([]); return; }
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${comp}/figure-draw`,
        { withCredentials: true }
      );
      setData(res.data);
      setRows(res.data.rows || []);
      setDirty(false);
    } catch (e) {
      setData(null);
      setRows([]);
    }
  }, [comp]);

  useEffect(() => { fetchData(); fetchLocks(); }, [fetchData, fetchLocks]);

  if (!comp) {
    return (
      <div className="text-stone-600 text-sm p-4">
        Select a competition to manage its Figure Draw.
      </div>
    );
  }

  const figureDrawType = data?.figure_draw_type;

  if (figureDrawType === 'Figure Draw Not Required') {
    return (
      <Card className="p-5 space-y-3 border-stone-200 bg-stone-50/50"
            data-testid="figure-draw-tile">
        <h3 className="text-lg font-semibold text-stone-800">Figure Draw</h3>
        <p className="text-sm text-stone-600">
          The competition&apos;s figure draw type is currently set to
          <span className="font-medium"> Figure Draw Not Required</span>.
          Change it in <span className="font-medium">Schedule Settings</span>
          to enable the figure-draw workflow.
        </p>
      </Card>
    );
  }

  if (PLACEHOLDER_TYPES.has(figureDrawType)) {
    return (
      <Card className="p-5 space-y-3 border-stone-200 bg-stone-50/50"
            data-testid="figure-draw-tile">
        <h3 className="text-lg font-semibold text-stone-800">Figure Draw</h3>
        <p className="text-sm text-stone-600">
          Type of Draw: <span className="font-medium">{figureDrawType}</span>
          {' '}— this draw type isn&apos;t yet supported in the manual draw
          panel. It will be wired up in a follow-up build.
        </p>
      </Card>
    );
  }

  if (!STANDARD_TYPES.has(figureDrawType) && !GROUP_DRAW_TYPES.has(figureDrawType)) {
    return null;
  }

  const isGroupDraw = GROUP_DRAW_TYPES.has(figureDrawType);
  const isAutoIssued = figureDrawType === 'Standard Draw issued as part of event notice';
  // Feb 2026 — plain "Standard Draw" (non-auto-issued, non-group) is
  // the Area/State/National path: Groups are drawn ~3 weeks out and
  // Foot ~1 week out. Surface that as a tiny guidance line under the
  // tagline so first-time organisers know the expected cadence.
  const isManualStandardDraw = figureDrawType === 'Standard Draw';

  // Tagline pieces
  const noteParts = [];
  if (isAutoIssued) {
    noteParts.push('Draw was created by system to go out with the Event Notice.');
  }
  if (data?.created_at) {
    noteParts.push(
      `Draw was last saved on ${_formatDrawWhen(data.created_at)} by ${data.created_by_label || 'unknown'}.`
    );
  }
  const tagline = `${noteParts.join(' ')} - Type of Draw: ${figureDrawType}`.trim();

  const updateRow = (rowId, patch) => {
    setRows(prev => prev.map(r => r.row_id === rowId ? { ...r, ...patch } : r));
    setDirty(true);
  };

  const autoDrawGroup = () => {
    setRows(prev => prev.map(r => {
      if (r.locked_group) return r;
      if ((r.num_groups || 1) <= 1) return r;
      const grp = 1 + Math.floor(Math.random() * r.num_groups);
      return { ...r, current_group: grp };
    }));
    setDirty(true);
    toast.success('Random groups drawn — review and Save Draw to persist.');
  };

  const autoDrawFoot = () => {
    setRows(prev => prev.map(r => {
      if (!r.foot_required) return r;
      if (r.predetermined) return r;
      return { ...r, current_foot: Math.random() < 0.5 ? 'Left' : 'Right' };
    }));
    setDirty(true);
    toast.success('Random feet drawn — review and Save Draw to persist.');
  };

  const resetDraw = () => {
    setRows(prev => prev.map(r => r.predetermined ? r : ({
      ...r,
      // Preserve locked_group for grades with only one group; only
      // wipe the group selection when the user can actually re-pick.
      current_group: r.locked_group || null,
      current_foot: 'Not yet drawn',
    })));
    setDirty(true);
  };

  const saveDraw = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/order-of-skate/${comp}/figure-draw`,
        {
          figure_draw_type: figureDrawType,
          rows: rows.map(r => ({
            row_id: r.row_id,
            grade_id: r.grade_id,
            group_number: r.current_group || null,
            foot: r.current_foot || null,
          })),
        },
        { withCredentials: true }
      );
      toast.success('Figure draw saved.');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save draw');
    } finally {
      setSaving(false);
    }
  };

  const downloadDoc = async () => {
    // Mid-state guard — the user can only generate the document when
    // either ALL foot-required rows have Left/Right drawn or ALL are
    // still "Not yet drawn". A mixed state is rejected with a modal
    // before we even hit the network.
    const footRows = rows.filter(r => r.foot_required && !r.predetermined);
    const drawn = footRows.filter(r => r.current_foot === 'Left' || r.current_foot === 'Right');
    const pending = footRows.filter(r => r.current_foot !== 'Left' && r.current_foot !== 'Right');
    if (drawn.length > 0 && pending.length > 0) {
      setIncompleteDialogOpen(true);
      return;
    }

    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/order-of-skate/${comp}/figure-draw/document`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      link.setAttribute('download', m ? m[1] : 'Figure_Draw.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download document');
    } finally {
      setDownloading(false);
    }
  };

  const performUnlock = async () => {
    setUnlocking(true);
    try {
      await axios.post(
        `${API}/competitions/${comp}/unlock`,
        { lock_type: 'figure_draw' },
        { withCredentials: true }
      );
      toast.success('Figure draw unlocked. Take care re-issuing the draw.');
      setUnlockDialogOpen(false);
      await fetchLocks();
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unlock');
    } finally {
      setUnlocking(false);
    }
  };

  const isLocked = !!lockInfo?.locked_at;
  const lockedWhen = isLocked ? _formatDrawWhen(lockInfo.locked_at) : '';
  const lockedBy = lockInfo?.locked_by_name || 'unknown';
  const drawTypeLabel = figureDrawType || 'figure';

  // ------------------------------------------------------------------
  // LOCKED VIEW — collapse the entire panel. Only the "Download" button
  // remains active plus the "Unlock" button for superuser / creator.
  // ------------------------------------------------------------------
  if (isLocked) {
    // The chevron (top-right) is the ONLY unlock gesture — clicking
    // the header row opens the confirmation dialog. No separate
    // Unlock button per user's Feb-2026 spec ("I'd prefer if this
    // didn't result in another button added"). Non-owners see the
    // padlock but no chevron.
    return (
      <Card
        className="p-5 space-y-3 border-stone-200 bg-stone-50/50"
        data-testid="figure-draw-tile"
      >
        <button
          type="button"
          onClick={() => { if (canUnlock) setUnlockDialogOpen(true); }}
          className={`w-full text-left flex items-start justify-between gap-3 ${canUnlock ? 'cursor-pointer' : 'cursor-default'}`}
          data-testid="figdraw-locked-header"
        >
          <div>
            <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
              Figure Draw
              <Lock className="h-4 w-4 text-stone-500" aria-label="Locked" data-testid="figure-draw-lock-icon" />
            </h3>
            <p className="text-sm text-stone-700 mt-1" data-testid="figure-draw-locked-note">
              Draw was last saved on <span className="font-medium">{lockedWhen}</span>{' '}
              by <span className="font-medium">{lockedBy}</span> — the draw has been
              distributed and is now locked to protect competitor
              expectations.
              {canUnlock && (
                <span className="ml-1 text-stone-500 italic">Click to unlock.</span>
              )}
            </p>
          </div>
          {canUnlock && (
            <ChevronRight className="h-5 w-5 text-stone-400 flex-shrink-0 mt-1" aria-label="Unlock" />
          )}
        </button>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={downloadDoc}
            disabled={downloading}
            data-testid="figdraw-download-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            {downloading ? 'Generating…' : 'Download Figure Draw Document'}
          </Button>
        </div>

        <AlertDialog open={unlockDialogOpen} onOpenChange={setUnlockDialogOpen}>
          <AlertDialogContent data-testid="figdraw-unlock-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                Unlock the figure draw?
              </AlertDialogTitle>
              <AlertDialogDescription>
                The <span className="font-medium">{drawTypeLabel}</span> draw was
                last saved on <span className="font-medium">{lockedWhen}</span>{' '}
                by <span className="font-medium">{lockedBy}</span>. Do you want
                to proceed?
                <br />
                <br />
                <strong>Yes</strong> — rework the draw.
                <br />
                <strong>No</strong> — use existing.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid="figdraw-unlock-no-btn"
                disabled={unlocking}
              >
                No
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={performUnlock}
                disabled={unlocking}
                className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
                data-testid="figdraw-unlock-yes-btn"
              >
                {unlocking ? 'Unlocking…' : 'Yes'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    );
  }

  // "Fully drawn" = every foot-required row has a Left/Right pick AND
  // every row that supports multiple groups has a group selected. When
  // that's true we collapse the panel by default; user can click the
  // header chevron to expand and edit.
  const isFullyDrawn = rows.length > 0 && rows.every((r) => {
    if (isGroupDraw) {
      const gd = r.group_draw || {};
      return !!gd.drawn_group_number
        && Array.isArray(gd.drawn_figure_ids)
        && gd.drawn_figure_ids.length === 2;
    }
    if (r.predetermined) return true;
    const groupOk = (r.num_groups || 1) <= 1 || !!r.current_group;
    const footOk = !r.foot_required
      || r.current_foot === 'Left'
      || r.current_foot === 'Right';
    return groupOk && footOk;
  });
  const collapsed = isFullyDrawn && !manuallyExpanded && !dirty;

  // Draw-Group-2-figures actions -------------------------------------
  const autoDrawGroups = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/order-of-skate/${comp}/figure-draw/group-draw/auto`,
        null,
        {
          params: { figure_draw_type: figureDrawType },
          withCredentials: true,
        }
      );
      toast.success('Draw generated — review below.');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to draw');
    } finally {
      setSaving(false);
    }
  };

  const saveGroupDraw = async () => {
    setSaving(true);
    try {
      const grades = rows
        .filter(r => r.group_draw && r.group_draw.drawn_group_number)
        .map(r => ({
          grade_id: r.grade_id,
          group_number: r.group_draw.drawn_group_number,
          figure_ids: r.group_draw.drawn_figure_ids || [],
          foot: r.group_draw.foot_draw || null,
        }));
      await axios.post(
        `${API}/order-of-skate/${comp}/figure-draw/group-draw`,
        { figure_draw_type: figureDrawType, grades },
        { withCredentials: true }
      );
      toast.success('Group draw saved.');
      setDirty(false);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const editGroupPick = (rowIdx, newGroupNumber) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== rowIdx) return r;
      const gd = { ...r.group_draw };
      gd.drawn_group_number = Number(newGroupNumber);
      // When switching group, wipe the picked figures — they belong
      // to the OLD group and would be invalid in the new one.
      gd.drawn_figure_ids = [];
      gd.drawn_lines = [];
      return { ...r, group_draw: gd };
    }));
    setDirty(true);
  };

  return (
    <Card
      className="p-5 space-y-4 border-stone-200 bg-stone-50/50"
      data-testid="figure-draw-tile"
    >
      <button
        type="button"
        onClick={() => {
          if (isFullyDrawn) setManuallyExpanded((v) => !v);
        }}
        className={`w-full text-left flex items-start justify-between gap-3 ${isFullyDrawn ? 'cursor-pointer' : 'cursor-default'}`}
        data-testid="figure-draw-header-toggle"
      >
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-stone-800">Figure Draw</h3>
          <p className="text-sm text-stone-700 mt-1">{tagline}</p>
          {isManualStandardDraw && (
            <p
              className="text-xs text-stone-500 mt-1 italic"
              data-testid="figure-draw-timing-hint"
            >
              Groups are typically drawn ~3 weeks before the competition; Foot ~1 week before.
            </p>
          )}
        </div>
        {isFullyDrawn && (
          collapsed
            ? <ChevronRight className="h-5 w-5 text-stone-400 flex-shrink-0 mt-1" aria-label="Expand" />
            : <ChevronDown className="h-5 w-5 text-stone-400 flex-shrink-0 mt-1" aria-label="Collapse" />
        )}
      </button>

      {/* When collapsed, keep the Download button visible on the same
          visual row as the header (Feb 2026 owner request). Rendered
          outside the header <button> so we don't nest interactive
          elements; the flex layout below keeps it inline. */}
      {collapsed && (
        <div className="flex justify-end -mt-2" data-testid="figdraw-collapsed-actions">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadDoc}
            disabled={downloading}
            data-testid="figdraw-collapsed-download-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            {downloading ? 'Generating…' : 'Download Figure Draw Document'}
          </Button>
        </div>
      )}

      {!collapsed && (rows.length === 0 ? (
        <p className="text-sm text-stone-600">
          No figure events are present in this competition&apos;s Order of Skate.
        </p>
      ) : isGroupDraw ? (
        <>
          {/* Draw-Group-2-figures table — one row per grade with a
              two-line Group cell: line 1 = group number, line 2 =
              figure pool (drawn figures highlighted). Foot draw is
              rolled once per grade and inlined onto the drawn
              figures via the render_figure_line helper on the backend. */}
          <div className="overflow-hidden border border-stone-200 rounded-lg">
            <table className="w-full text-sm" data-testid="figure-draw-group-table">
              <thead className="bg-stone-100 text-stone-700">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-1/3">Grade</th>
                  <th className="text-left px-3 py-2 font-medium">Group / Figures Drawn</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, rowIdx) => {
                  const gd = r.group_draw || { groups: [], drawn_lines: [] };
                  const drawnIds = new Set(gd.drawn_figure_ids || []);
                  const currentGroup = gd.groups.find(
                    (g) => g.group_number === gd.drawn_group_number
                  );
                  return (
                    <tr key={r.row_id} className="border-t border-stone-200 align-top">
                      <td className="px-3 py-3 text-stone-800"
                          data-testid={`figdraw-group-row-${r.row_id}-name`}>
                        {r.display_name}
                      </td>
                      <td className="px-3 py-3 space-y-1.5">
                        {/* Line 1: Group N (editable via dropdown) */}
                        <div className="flex items-center gap-2">
                          <span className="text-stone-600 text-xs">Group</span>
                          <select
                            value={gd.drawn_group_number || ''}
                            onChange={(e) => editGroupPick(rowIdx, e.target.value)}
                            className="border border-stone-300 rounded px-2 py-0.5 text-sm bg-white"
                            data-testid={`figdraw-group-row-${r.row_id}-group-select`}
                          >
                            <option value="">—</option>
                            {gd.groups.map((g) => (
                              <option key={g.group_number} value={g.group_number}>
                                {g.group_number}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Line 2: figure pool for the drawn group */}
                        {currentGroup && (
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
                            {currentGroup.figures.map((f) => {
                              const isDrawn = drawnIds.has(f.figure_id);
                              // Prefer the backend-rendered line (foot
                              // already applied) for drawn figures; the
                              // pool default for non-drawn.
                              const drawnLine = (gd.drawn_lines || []).find(
                                (dl) => dl.figure_id === f.figure_id
                              );
                              const label = isDrawn && drawnLine
                                ? `${drawnLine.number_display} - ${drawnLine.description_display}`
                                : `${f.number_display} - ${f.description}`;
                              return (
                                <span
                                  key={f.figure_id}
                                  className={
                                    isDrawn
                                      ? 'font-semibold text-[#5C4033]'
                                      : 'text-stone-500'
                                  }
                                  title={f.figure_name || undefined}
                                  data-testid={`figdraw-group-row-${r.row_id}-fig-${f.figure_id}${isDrawn ? '-drawn' : ''}`}
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Group-draw button row */}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              onClick={autoDrawGroups}
              variant="outline"
              disabled={saving}
              data-testid="figdraw-group-autodraw-btn"
            >
              <Shuffle className="mr-2 h-4 w-4" />
              {isFullyDrawn ? 'Redraw All' : 'Draw'}
            </Button>
            <Button
              type="button"
              onClick={saveGroupDraw}
              disabled={!dirty || saving}
              className="btn-primary"
              data-testid="figdraw-group-save-btn"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
            <Button
              type="button"
              onClick={downloadDoc}
              disabled={downloading || !isFullyDrawn}
              variant="outline"
              data-testid="figdraw-group-download-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              {downloading ? 'Generating…' : 'Download Figure Draw Document'}
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Draw table — same formatting cues as the fees table */}
          <div className="overflow-hidden border border-stone-200 rounded-lg">
            <table className="w-full text-sm" data-testid="figure-draw-table">
              <thead className="bg-stone-100 text-stone-700">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Grade</th>
                  <th className="text-left px-3 py-2 font-medium">Group</th>
                  <th className="text-left px-3 py-2 font-medium">Foot</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.row_id} className="border-t border-stone-200">
                    <td className="px-3 py-2 align-top text-stone-800"
                        data-testid={`figdraw-row-${r.row_id}-name`}>
                      {r.display_name}
                      {r.predetermined && (
                        <span className="ml-2 text-xs text-stone-500">
                          (no draw required)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-3">
                        {[...Array(r.num_groups || 1)].map((_, i) => {
                          const groupNum = i + 1;
                          // Group radios are read-only when the grade
                          // has only one group (locked_group set) —
                          // no roll to make.
                          const disabled = !!r.locked_group;
                          return (
                            <label key={groupNum}
                                   className={`inline-flex items-center gap-1 ${disabled ? 'text-stone-500' : 'text-stone-800'}`}>
                              <input
                                type="radio"
                                name={`group-${r.row_id}`}
                                value={groupNum}
                                disabled={disabled}
                                checked={r.current_group === groupNum}
                                onChange={() => updateRow(r.row_id, { current_group: groupNum })}
                                data-testid={`figdraw-row-${r.row_id}-group-${groupNum}`}
                              />
                              {groupNum}
                            </label>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-3">
                        {['Left', 'Right', 'Not yet drawn'].map((opt) => {
                          const disabled = !r.foot_required || r.predetermined;
                          return (
                            <label key={opt}
                                   className={`inline-flex items-center gap-1 ${disabled ? 'text-stone-500' : 'text-stone-800'}`}>
                              <input
                                type="radio"
                                name={`foot-${r.row_id}`}
                                value={opt}
                                disabled={disabled}
                                checked={r.current_foot === opt}
                                onChange={() => updateRow(r.row_id, { current_foot: opt })}
                                data-testid={`figdraw-row-${r.row_id}-foot-${opt.replace(/\s+/g, '-')}`}
                              />
                              {opt}
                            </label>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="button"
              onClick={autoDrawGroup}
              variant="outline"
              data-testid="figdraw-auto-group-btn"
              disabled={isAutoIssued && !!data?.created_at}
              title={isAutoIssued && !!data?.created_at ? 'Already drawn by system' : undefined}
            >
              <Shuffle className="mr-2 h-4 w-4" />
              Draw Group
            </Button>
            <Button
              type="button"
              onClick={autoDrawFoot}
              variant="outline"
              data-testid="figdraw-auto-foot-btn"
              disabled={isAutoIssued && !!data?.created_at}
              title={isAutoIssued && !!data?.created_at ? 'Already drawn by system' : undefined}
            >
              <Shuffle className="mr-2 h-4 w-4" />
              Draw Foot
            </Button>
            <Button
              type="button"
              onClick={resetDraw}
              variant="outline"
              data-testid="figdraw-reset-btn"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button
              type="button"
              onClick={saveDraw}
              disabled={!dirty || saving}
              className="btn-primary"
              data-testid="figdraw-save-btn"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving…' : 'Save Draw'}
            </Button>
            <Button
              type="button"
              onClick={downloadDoc}
              disabled={downloading}
              variant="outline"
              data-testid="figdraw-download-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              {downloading ? 'Generating…' : 'Download Figure Draw Document'}
            </Button>
          </div>
        </>
      ))}

      <AlertDialog open={incompleteDialogOpen} onOpenChange={setIncompleteDialogOpen}>
        <AlertDialogContent data-testid="figdraw-incomplete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Draw is not correctly completed
            </AlertDialogTitle>
            <AlertDialogDescription>
              When completing a figure draw, I can only create a document when
              all grades have a foot draw selected, or when all grades are set
              to &quot;Not yet drawn&quot;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setIncompleteDialogOpen(false)}
              data-testid="figdraw-incomplete-ok-btn"
            >
              Okay
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
